import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { doc, updateDoc } from 'firebase/firestore'
import * as React from 'react'
import banks from '../../assets/banks.png'
import stripeConnect from '../../assets/stripe-connect.png'
import { firebaseAuth, firestore } from '../../config/firebase'
import { Button } from '../../shared-components/Button'
import { ErrorMessage } from '../../shared-components/ErrorMessage'
import { useScreenTracking } from '../../shared-hooks/useScreenTracking'
import { USERS_COLLECTION } from '../../shared-hooks/useUserData'
import { track } from '../../utils/analytics'
import { parseRequestError } from '../../utils/sso'
import { SignupCard } from './SignupCard'
import { REQUEST_OPTIONS, SignupStep } from './SignupFlow'

const STRIPE_URL =
  'https://us-central1-infra-test-308817.cloudfunctions.net/stripe'

const formatCardNumber = (cardNumber: string): string => {
  return cardNumber
    .replace(/\D*/g, '')
    .replace(/(\d{4})/g, '$1 ')
    .trim()
    .slice(0, 19)
}

const formatExpiry = (expiry: string): string => {
  return expiry
    .replace(/\D*/g, '')
    .replace(/(\d{2})/g, '$1/')
    .slice(0, 5)
}

const formatCvc = (cvc: string): string => {
  return cvc.replace(/\D*/g, '').slice(0, 3)
}

type StripeProps = {
  setSignupStep: (step: SignupStep) => void
}

export const Stripe = ({ setSignupStep }: StripeProps): JSX.Element => {
  useScreenTracking('StripeView', 'View stripe screen')

  const [cardNumber, setCardNumber] = React.useState('')
  const [expiry, setExpiry] = React.useState('')
  const [cvc, setCvc] = React.useState('')

  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setErrorMessage('')

    track('ConnectStripeAttempt', 'Connect stripe attempt')

    try {
      const idToken = await firebaseAuth.currentUser.getIdToken()
      const headers = {
        ...REQUEST_OPTIONS.headers,
        Authorization: `Bearer ${idToken}`,
      }

      const [expMonth, expYear] = expiry.split('/')
      const body = {
        debit_card: {
          number: cardNumber.replace(/\D*/g, ''),
          exp_month: expMonth,
          exp_year: expYear,
          cvc,
          currency: 'usd',
        },
      }

      const response = await fetch(STRIPE_URL, {
        ...REQUEST_OPTIONS,
        headers,
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errorMessage = parseRequestError(
          response.status,
          {},
          'Card details are incorrect. Please try again.',
          ''
        )
        throw new Error(errorMessage)
      }

      track('ConnectStripeSuccess', 'Connect stripe success')
      const userDocRef = doc(
        firestore,
        USERS_COLLECTION,
        firebaseAuth.currentUser.uid
      )
      await updateDoc(userDocRef, { status: 'active' })
      setSignupStep(SignupStep.Finished)
    } catch (error) {
      track('ConnectStripeFailure', 'Connect stripe failure')
      setIsSubmitting(false)
      setErrorMessage(error?.message)
    }
  }

  return (
    <SignupCard
      headerComponent={
        <div className='float-right relative -top-1.5 -right-4'>
          <IconButton
            size='large'
            disabled={isSubmitting}
            onClick={() => setSignupStep(SignupStep.Plaid)}
          >
            <CloseIcon fontSize='inherit' />
          </IconButton>
        </div>
      }
      heading='Enable transactions'
    >
      <form className='-mt-3 flex flex-col gap-4' onSubmit={onSubmit}>
        <img className='-ml-2' width='85%' src={stripeConnect} />
        <StripeInput
          pattern='\d{4} \d{4} \d{4} \d{4}'
          label='Card number'
          iconSrc={banks}
          placeholder='1234 1234 1234 1234'
          autoComplete='cc-number'
          value={cardNumber}
          onChange={(event) =>
            setCardNumber(formatCardNumber(event.target.value))
          }
          disabled={isSubmitting}
          maxLength={19}
          required
        />
        <div className='flex gap-3 -mt-1'>
          <StripeInput
            pattern='\d{2}/\d{2}'
            label='Expiry'
            placeholder='MM/YY'
            autoComplete='cc-exp'
            value={expiry}
            onChange={(event) => setExpiry(formatExpiry(event.target.value))}
            disabled={isSubmitting}
            maxLength={5}
            required
          />
          <StripeInput
            pattern='\d{3}'
            label='CVC'
            placeholder='CVC'
            autoComplete='cc-csc'
            value={cvc}
            onChange={(event) => setCvc(formatCvc(event.target.value))}
            disabled={isSubmitting}
            maxLength={3}
            required
          />
        </div>
        <ErrorMessage>{errorMessage}</ErrorMessage>
        <Button type='submit' isSubmitting={isSubmitting}>
          Connect Debit Card
        </Button>
      </form>
    </SignupCard>
  )
}

interface StripeInputProps extends React.ComponentPropsWithRef<'input'> {
  label: string
  iconSrc?: string
}

const StripeInput = ({
  label,
  iconSrc,
  ...props
}: StripeInputProps): JSX.Element => {
  return (
    <label className='flex-1 relative font-medium text-sm text-slate-600'>
      {label}
      <div className='block'>
        {iconSrc && (
          <img className='inline absolute right-2 top-10' src={iconSrc} />
        )}
        <input
          className={`mt-1 appearance-none rounded-md w-full border-2 border-neutral-200 placeholder-gray-400 shadow-sm p-3`}
          type='text'
          {...props}
        />
      </div>
    </label>
  )
}
